/**
 * Facts about the site and the company that appear in more than one place.
 *
 * Anything a lawyer, a registrar or an app store would care about lives here
 * rather than being typed into each page, so there is exactly one line to
 * change when the company address or the support address changes.
 */

/*
 * The address that actually serves the site, and therefore what canonical
 * tags, hreflang and Open Graph URLs must point at.
 *
 * www, not the bare domain, because twomilks.app redirects to it — the same
 * convention as google.com, facebook.com and buildpassing.com. A canonical URL
 * that redirects wastes a hop and muddies the signal.
 *
 * PRODUCT.domain below stays bare: that one is read by humans, not machines,
 * and it is the form the marketing kit prints.
 */
export { SITE_ORIGIN } from '../../shared/seo';

export const PRODUCT = {
  /*
   * The brand name never translates. "Two Milks" in English and in Spanish,
   * always — the Spanish copy says Two Milks, not "Dos Leches".
   */
  name: 'Two Milks',
  domain: 'twomilks.app',
  instagram: 'twomilks.app',
  instagramUrl: 'https://instagram.com/twomilks.app',
} as const;

export const COMPANY = {
  /**
   * The name that answers for this product in law.
   *
   * This does NOT have to be a registered company. If you operate as an
   * individual, put your own full legal name here — a sole trader is a
   * perfectly valid data controller, and both app stores issue individual
   * developer accounts. "BuildPassing" on its own is a trading name, which is
   * fine on the site but is not what a store or a regulator will match against.
   *
   * Before submitting to either app store this must be the exact name on the
   * developer account. See docs/legal.html.
   */
  legalName: 'BuildPassing',

  /** The name shown in ordinary copy — the footer, the emails, the marketing. */
  tradeName: 'BuildPassing',

  /**
   * Postal address of whoever is responsible for the data.
   *
   * Deliberately EMPTY until there is a real one. The legal pages check for
   * that and fall back to the email contact rather than printing a placeholder
   * — a made-up address in a privacy notice is worse than no address, because
   * it looks like a considered answer.
   *
   * When you do need one:
   *   - Mexican data protection law expects the responsable's domicilio in the
   *     aviso de privacidad.
   *   - Both app stores require an address on the developer account, and for
   *     EU distribution they publish it on the store listing under the Digital
   *     Services Act trader rules.
   * Neither requires a company — a personal or fiscal address is enough. See
   * docs/legal.html for the options.
   */
  address: '',

  /** Where the company is established. Used for the governing-law clause. */
  country: 'Mexico',

  domain: 'buildpassing.com',
  url: 'https://buildpassing.com',
  email: 'hola@buildpassing.com',
  privacyEmail: 'hola@buildpassing.com',
  supportEmail: 'hola@buildpassing.com',
} as const;

/** True once a real postal address has been filled in above. */
export const hasPostalAddress = (): boolean => COMPANY.address.trim().length > 0;

/**
 * The date the current legal texts took effect. Update it whenever a legal page
 * changes materially — the privacy notice promises we will.
 */
export const LEGAL_LAST_UPDATED = '2026-08-25';
